import { Light as SyntaxHighlighter } from "react-syntax-highlighter";
import { useAviaryTheme, Button } from "@fullscript/aviary-web";
import type { ReactNode } from "react";
import defaultStyle from "react-syntax-highlighter/dist/cjs/styles/hljs/default-style";
import bash from "react-syntax-highlighter/dist/cjs/languages/hljs/bash";
import xml from "react-syntax-highlighter/dist/cjs/languages/hljs/xml";
import json from "react-syntax-highlighter/dist/cjs/languages/hljs/json";
import javascript from "react-syntax-highlighter/dist/cjs/languages/hljs/javascript";
import typescript from "react-syntax-highlighter/dist/cjs/languages/hljs/typescript";
import http from "react-syntax-highlighter/dist/cjs/languages/hljs/http";
import * as styles from "./CustomCode.styles";
import { faLink } from "@fortawesome/pro-regular-svg-icons";
import { useToast } from "@/aviaryTmp/Toast/useToast";

// Register languages once at module level (only in browser environment)
if (typeof window !== "undefined") {
  SyntaxHighlighter.registerLanguage("json", json);
  SyntaxHighlighter.registerLanguage("javascript", javascript);
  SyntaxHighlighter.registerLanguage("typescript", typescript);
  SyntaxHighlighter.registerLanguage("xml", xml);
  SyntaxHighlighter.registerLanguage("bash", bash);
  SyntaxHighlighter.registerLanguage("http", http);
}

const LANGUAGE = {
  // highlight.js doesn't support html for some reason, so we use xml instead
  "language-html": "xml",
  "language-js": "javascript",
  "language-json": "json",
  "language-ts": "typescript",
  "language-shell": "bash",
  "language-sh": "bash",
  "language-http": "http",
  "language-curl": "http",
  "language-text": "bash",
} as const;

type LanguageKey = keyof typeof LANGUAGE;

type Props = {
  children: ReactNode;
  className?: LanguageKey;
  withHeader?: boolean;
};

/* eslint-disable no-restricted-syntax */
// SyntaxHighlighter component can not accept css prop because it's a third party component.

const CustomCode = ({ children, className, withHeader }: Props) => {
  const { makeToast } = useToast();
  const theme = useAviaryTheme();

  const handleClickCopy = () => {
    if (!children) return;
    navigator.clipboard.writeText(children.toString());
    makeToast("success", "Successfully copied to clipboard");
  };

  const customStyle = {
    background: theme.currentTheme.system.backgroundMutedHover,
    width: "100%",
    color: theme.currentTheme.text.body,
    borderRadius: withHeader ? null : "0.5rem",
    borderBottomLeftRadius: withHeader ? "0.5rem" : null,
    borderBottomRightRadius: withHeader ? "0.5rem" : null,
    display: "block",
    padding: "1rem",
    marginBottom: "1rem",
    overflowX: "auto",
  };

  return (
    <div css={styles.container} data-testid="block-code">
      {/* @ts-expect-error The type definition is something wrong */}
      <SyntaxHighlighter
        PreTag="span"
        language={LANGUAGE[className ?? "language-text"]}
        style={defaultStyle}
        customStyle={customStyle}
      >
        {children}
      </SyntaxHighlighter>
      {!withHeader && (
        <Button
          variant="secondary"
          size="small"
          customCss={styles.copyButton}
          onClick={handleClickCopy}
          leftIcon={faLink}
        >
          Copy
        </Button>
      )}
    </div>
  );
};

/* eslint-enable no-restricted-syntax */

export { CustomCode };
